// validate_cycle (RS from 0016, AA on older sandboxes; CORE-055/056): the sixteen checks, gas complete or pre-arranged,
// the first-day worksite examination and the previous stop's resolution, for this
// cycle only. Class A (0030): the examination at every validation where the rules say so,
// the RS's declaration to stay in the work area and the first-gas-test delegation.
import { useId, useState } from 'react'
import { ErrorBanner, RuleRefs } from '@/components/evidence'
import { Banner, Field, Select, Spinner, TextArea } from '@/components/ui'
import { AA_VALIDATION_CHECKS, GAS_SPECIES, GAS_TEST_KINDS, SUSPENSION_REASONS } from '@/domain/catalogue'
import type { CycleView, Registry } from '@/domain/types'
import type { ActionPanelProps } from '@/features/contracts'
import { fmtDateTime, fromLocalInput, inputTimeArg, labelFor, toLocalInput } from '@/lib/format'
import { usePolicy, useRegistry } from '@/lib/queries'
import { PanelFrame } from '../frame'
import { usePanelClock, usePanelCommand } from '../hooks'
import {
  allowedUnits,
  buildValidationChecks,
  classAValidationNeeds,
  type CheckAnswer,
  type CheckResult,
  currentAuthorisation,
  cycleForAction,
  defaultValidationStart,
  defaultValidationWindow,
  earliestValidationStart,
  emptyAnswers,
  FALLBACK_NORMAL_VALIDATION_HOURS,
  floorToMinute,
  formatDuration,
  gasPlanOf,
  isGasStop,
  joinNumbers,
  minutesBetween,
  observedInPeriod,
  parseIsoMs,
  msToIso,
  policyParameter,
  predecessorOf,
  requiresFirstDayExam,
  resolvingGasTests,
  revisionOf,
  speciesLabel,
  stopsToResolve,
  testsNewestFirst,
  usableNumber,
  VALIDATION_MAX_BACKDATE_MINUTES,
  validationCheckProblems,
} from '../logic'
import {
  Choice,
  CycleHeader,
  DateTimeField,
  GasClassificationBadge,
  MissingCycle,
  PolicyNote,
  SigningStatement,
  TextField,
  TickRow,
  WindowText,
} from '../parts'

type ResultOption = { value: CheckResult; label: string }

const STANDARD_OPTIONS: readonly ResultOption[] = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'not_applicable', label: 'Not applicable' },
]
const GAS_REQUIRED_OPTIONS: readonly ResultOption[] = [
  { value: 'confirmed', label: 'Complete' },
  { value: 'prearranged', label: 'Pre-arranged' },
]
const GAS_NOT_REQUIRED_OPTIONS: readonly ResultOption[] = [{ value: 'not_applicable', label: 'Not applicable' }]

function CycleGasTests({ cycle }: { cycle: CycleView }) {
  const tests = testsNewestFirst(cycle.gas_tests)
  if (tests.length === 0) {
    return (
      <Banner tone="warn" title="No gas test in this cycle yet" className="mt-3">
        Choose Pre-arranged, or ask the AGT to record the test first.
      </Banner>
    )
  }
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Gas tests recorded in this cycle (newest first)</p>
      {tests.map((t) => (
        <div key={t.id} className="space-y-1 rounded-lg border border-line bg-paper p-2 text-sm">
          <p className="text-ink">
            {labelFor(GAS_TEST_KINDS, t.test_kind)}, observed {fmtDateTime(t.reported_observed_at)}
          </p>
          <p className="text-ink-soft">Sample location: {t.sample_location}</p>
          <GasClassificationBadge test={t} />
        </div>
      ))}
      <p className="text-xs text-ink-soft">The server checks for an acceptable test in this cycle with no unsafe test after it.</p>
    </div>
  )
}

function Prearrangement({
  registry,
  testers,
  tester,
  setTester,
  plannedAt,
  setPlannedAt,
  sampleLocation,
  setSampleLocation,
  requiredSpecies,
  optionalSpecies,
  extraSpecies,
  setExtraSpecies,
}: {
  registry: { isLoading: boolean; error: unknown }
  testers: Registry['people']
  tester: string
  setTester: (v: string) => void
  plannedAt: string
  setPlannedAt: (v: string) => void
  sampleLocation: string
  setSampleLocation: (v: string) => void
  requiredSpecies: string[]
  optionalSpecies: string[]
  extraSpecies: string[]
  setExtraSpecies: (v: string[]) => void
}) {
  const testerId = useId()
  return (
    <div className="mt-3 rounded-lg border border-warn/30 bg-warn-light p-3">
      <p className="mb-3 text-sm text-ink">
        Pre-arranged is allowed for validation. Work still cannot start until the AGT records an acceptable test in this cycle.
      </p>
      {registry.isLoading ? (
        <p className="mb-4 flex items-center gap-2 text-sm text-ink-soft">
          <Spinner small /> Loading gas testers...
        </p>
      ) : registry.error ? (
        <div className="mb-4">
          <ErrorBanner error={registry.error} title="Could not load the list of gas testers" />
        </div>
      ) : (
        <Field
          label="Gas tester (AGT)"
          required
          htmlFor={testerId}
          hint={testers.length === 0 ? 'No one with an AGT certificate is listed for you.' : 'People holding an AGT certificate. The server checks it covers this area.'}
        >
          <Select
            id={testerId}
            value={tester}
            onChange={(e) => setTester(e.target.value)}
            placeholder="Choose the tester"
            options={testers.map((p) => ({ value: p.id, label: `${p.display_name}${p.organisation ? ` (${p.organisation})` : ''}` }))}
          />
        </Field>
      )}
      <DateTimeField
        label="Planned test time"
        required
        value={plannedAt}
        onChange={setPlannedAt}
        hint="Before work starts, and no later than the validation end."
      />
      <TextField label="Sample location" required value={sampleLocation} onChange={setSampleLocation} placeholder="e.g. at pipe support SYN-PS-101" />
      <fieldset>
        <legend className="text-sm font-semibold text-ink">Gases to test</legend>
        <p className="mt-1 text-sm text-ink">
          {requiredSpecies.length > 0 ? `Required by the gas plan: ${requiredSpecies.map(speciesLabel).join(', ')}` : 'The gas plan lists no gases.'}
        </p>
        {optionalSpecies.length > 0 ? (
          <div className="mt-2 divide-y divide-line rounded-lg border border-line bg-white">
            {optionalSpecies.map((code) => (
              <TickRow
                key={code}
                checked={extraSpecies.includes(code)}
                onChange={(v) => setExtraSpecies(v ? [...extraSpecies, code] : extraSpecies.filter((c) => c !== code))}
                label={`Also test ${speciesLabel(code)}`}
              />
            ))}
          </div>
        ) : null}
      </fieldset>
    </div>
  )
}

export function ValidateCyclePanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cycle = cycleForAction(bundle, action)
  const now = usePanelClock(bundle)
  const policy = usePolicy(bundle.policy.version_id)
  const registry = useRegistry()
  const run = usePanelCommand('validate_cycle', bundle, onDone)
  const auth = currentAuthorisation(bundle)
  const revision = revisionOf(bundle, cycle)
  const gasPlan = gasPlanOf(revision)
  const firstDayExam = cycle ? requiresFirstDayExam(revision, cycle) : false
  // Every stop since the last validated cycle, not only the previous cycle's: a cycle that
  // lapsed without validation does not clear the stop before it (server: cyc_stops_to_resolve).
  const priorStops = cycle ? stopsToResolve(bundle, cycle) : []
  const stoppedCycles = [...new Set(priorStops.map((s) => s.cycle.cycle_number))]
  const needsResolution = priorStops.length > 0
  const needsGasResolution = priorStops.some((s) => isGasStop(s.stop))
  const candidates = cycle && needsResolution ? resolvingGasTests(bundle, cycle, priorStops) : []
  const predecessor = cycle ? predecessorOf(bundle, cycle) : null
  const stopBeforeLapse = needsResolution && predecessor !== null && !stoppedCycles.includes(predecessor.cycle_number)

  const [initial] = useState(() => {
    if (!cycle) return null
    const nowMs = now()
    // Validity starts at the cycle start while the server still accepts that (else now),
    // sent exactly while the input still shows its minute. A gas test the AGT took after
    // the cycle request is then inside the validated period, as PH acceptance requires.
    const start = defaultValidationStart(nowMs, cycle.requested_from)
    return { ...defaultValidationWindow(nowMs, cycle), startExact: msToIso(start) }
  })
  const [from, setFrom] = useState(() => (initial ? toLocalInput(initial.startExact) : ''))
  const [to, setTo] = useState(() => (initial ? toLocalInput(msToIso(initial.to)) : ''))
  const [answers, setAnswers] = useState<Record<string, CheckAnswer>>(() => emptyAnswers(AA_VALIDATION_CHECKS))
  const [tester, setTester] = useState('')
  const [plannedAt, setPlannedAt] = useState(from)
  const [sampleLocation, setSampleLocation] = useState('')
  const [extraSpecies, setExtraSpecies] = useState<string[]>([])
  const [examAt, setExamAt] = useState(() => toLocalInput(msToIso(floorToMinute(now()))))
  const [findings, setFindings] = useState('')
  const [stopNote, setStopNote] = useState('')
  const [resolvingTest, setResolvingTest] = useState('')
  const [tried, setTried] = useState(false)
  const [staysInArea, setStaysInArea] = useState(false)
  const [delegationNote, setDelegationNote] = useState('')
  const delegationId = useId()
  const findingsId = useId()
  const stopNoteId = useId()
  const resolvingId = useId()

  const fromIso = inputTimeArg(from, initial?.startExact)
  const toIso = fromLocalInput(to)
  const mins = minutesBetween(fromIso, toIso)
  const normalParam = policyParameter(policy.data, 'validation.normal_hours')
  const normalHours = usableNumber(normalParam) ?? FALLBACK_NORMAL_VALIDATION_HOURS
  const units = allowedUnits(policyParameter(policy.data, 'gas.species_units'))
  const requiredSpecies = gasPlan?.species ?? []
  const optionalSpecies = (units ? Object.keys(units) : GAS_SPECIES.map((g) => g.code)).filter((c) => !requiredSpecies.includes(c))
  const gasAnswer = answers.gas_tests?.result ?? null
  const testers = [...(registry.data?.people ?? [])]
    .filter((p) => p.roles.includes('AGT'))
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
  // Only values still shown on screen are sent (lists can refresh while the form is open).
  const testerChosen = testers.some((p) => p.id === tester) ? tester : ''
  const resolvingChosen = candidates.some((t) => t.id === resolvingTest) ? resolvingTest : ''
  const testerPerson = testers.find((p) => p.id === testerChosen)
  const classA = cycle
    ? classAValidationNeeds(bundle, cycle, gasPlan && answers.gas_tests?.result === 'prearranged' && testerPerson ? { id: testerPerson.id, name: testerPerson.display_name } : null)
    : null
  const needsExam = firstDayExam || (classA?.dailyExam ?? false)
  const delegating = classA?.otherFirstTester != null && gasPlan != null
  const extraChosen = extraSpecies.filter((s) => optionalSpecies.includes(s))

  function setAnswer(code: string, patch: Partial<CheckAnswer>) {
    setAnswers((prev) => ({ ...prev, [code]: { ...(prev[code] ?? { result: null, note: '' }), ...patch } }))
  }

  // The latest gas test of this cycle against the period being signed: a test taken
  // before the period does not count for starting work (accept_cycle).
  const latestGas = cycle ? testsNewestFirst(cycle.gas_tests)[0] ?? null : null
  const latestGasInPeriod = latestGas && fromIso && toIso ? observedInPeriod(latestGas, { validation_from: fromIso, validation_to: toIso }) : null
  const fromMs = parseIsoMs(fromIso)
  const earliestMs = cycle ? earliestValidationStart(now(), cycle.requested_from) : null

  const problems: string[] = []
  if (!fromIso) problems.push('Enter the validation start.')
  if (fromMs !== null && earliestMs !== null && fromMs < earliestMs) {
    problems.push(
      `The validation cannot start before ${fmtDateTime(msToIso(earliestMs))}: not before the cycle start, and not more than ${VALIDATION_MAX_BACKDATE_MINUTES} minutes ago.`,
    )
  }
  if (!toIso) problems.push('Enter the validation end.')
  if (mins !== null && mins <= 0) problems.push('The validation end must be after its start.')
  problems.push(...validationCheckProblems(AA_VALIDATION_CHECKS, answers))
  if (gasAnswer === 'prearranged') {
    if (!testerChosen) problems.push('Choose the gas tester for the pre-arranged test.')
    if (!fromLocalInput(plannedAt)) problems.push('Enter when the pre-arranged gas test will be done.')
    if (!sampleLocation.trim()) problems.push('Enter where the pre-arranged gas sample will be taken.')
  }
  if (needsExam) {
    if (!fromLocalInput(examAt)) problems.push('Enter when you examined the worksite.')
    if (!findings.trim()) problems.push('Enter your worksite examination findings.')
  }
  if (classA) {
    if (!staysInArea) problems.push('Confirm that you will stay in the work area while this Class A work goes on.')
    if (classA.hcaUnresolved) problems.push('Class A work in a hydrocarbon area cannot be validated until the examination rule there is decided.')
    if (classA.examRuleBlocked) problems.push('The Class A examination rule is not usable under the rules in use.')
    if (delegating && classA.delegationAllowed === false) {
      problems.push(`For Class A you do the first gas test of the day yourself; the rules in use do not allow delegating it to ${classA.otherFirstTester?.name}.`)
    }
    if (delegating && classA.delegationAllowed !== false && !delegationNote.trim()) {
      problems.push(`Record that you delegated the first gas test to ${classA.otherFirstTester?.name}, and why.`)
    }
  }
  if (needsResolution && !stopNote.trim()) problems.push('Explain how the previous stop was resolved.')
  if (needsGasResolution && !resolvingChosen) {
    problems.push(
      candidates.length > 0
        ? 'Choose the gas test that shows it is safe now.'
        : 'An acceptable gas test recorded after the unsafe result is needed first.',
    )
  }

  function submit() {
    setTried(true)
    if (!cycle || problems.length > 0 || !fromIso || !toIso) return
    void run.submit({
      permit_id: bundle.permit.id,
      cycle_id: cycle.id,
      validation_from: fromIso,
      validation_to: toIso,
      checks: buildValidationChecks(AA_VALIDATION_CHECKS, answers),
      prearrangement:
        gasAnswer === 'prearranged'
          ? {
              tester_person_id: testerChosen,
              planned_at: fromLocalInput(plannedAt) ?? '',
              sample_location: sampleLocation.trim(),
              species: [...new Set([...requiredSpecies, ...extraChosen])],
            }
          : null,
      examination: needsExam ? { reported_observed_at: fromLocalInput(examAt) ?? '', findings: findings.trim() } : null,
      stop_resolution: needsResolution ? { note: stopNote.trim(), resolving_gas_test_id: resolvingChosen || null } : null,
      ...(classA ? { class_a: { rs_remains_in_area: staysInArea, first_gas_test_delegation: delegating ? delegationNote.trim() : null } } : {}),
    })
  }

  const n = cycle?.cycle_number ?? 0

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel="Sign validation"
      blockedNote={cycle ? null : 'Cycle not found.'}
    >
      {cycle ? <CycleHeader cycle={cycle} /> : <MissingCycle />}

      {cycle?.preissue ? (
        <p className="mb-4 text-sm text-ink">
          Pre-issued for this cycle by {cycle.preissue.attestation.signer.display_name},{' '}
          {fmtDateTime(cycle.preissue.attestation.server_signed_at)}. Validation is a separate signature: the same RS or a
          second RS may give it.
        </p>
      ) : cycle ? (
        <Banner tone="warn" title="No RS pre-issue on this cycle" className="mb-4">
          Validation needs the RS pre-issue of this cycle; an earlier cycle's pre-issue does not count (CORE-052).
        </Banner>
      ) : null}

      {cycle && needsResolution ? (
        <section className="mb-4 rounded-lg border-2 border-bad/40 bg-white p-3">
          <h3 className="display mb-1 text-base font-semibold text-ink">
            {stoppedCycles.length === 1 ? `Cycle ${stoppedCycles[0]} was stopped` : `Cycles ${joinNumbers(stoppedCycles)} were stopped`}
            <RuleRefs ruleIds={['CORE-040', 'CORE-063']} />
          </h3>
          <ul className="mb-3 list-disc space-y-1 pl-5 text-sm text-ink">
            {priorStops.map(({ stop: s, cycle: c }) => (
              <li key={s.id}>
                <strong>{labelFor(SUSPENSION_REASONS, s.reason_code)}</strong>: {s.reported_condition} (cycle {c.cycle_number},{' '}
                {fmtDateTime(s.reported_observed_at)})
              </li>
            ))}
          </ul>
          {stopBeforeLapse ? (
            <p className="mb-2 text-sm text-ink">
              Cycle {predecessor?.cycle_number} ended without a validation, so it did not clear this stop. It is resolved here.
            </p>
          ) : null}
          <p className="mb-3 text-xs text-ink-soft">The stop stays on the record. Say how its cause was dealt with before you validate this new cycle.</p>
          <Field label="How the cause was resolved" required htmlFor={stopNoteId}>
            <TextArea id={stopNoteId} value={stopNote} onChange={(e) => setStopNote(e.target.value)} />
          </Field>
          {candidates.length > 0 ? (
            <Field
              label={needsGasResolution ? 'Gas test that shows it is safe now' : 'Gas test that supports the restart (optional)'}
              required={needsGasResolution}
              htmlFor={resolvingId}
              hint={
                needsGasResolution
                  ? `Only acceptable tests taken on cycle ${cycle.cycle_number} after the unsafe reading are listed. A pre-arranged test, or a test on an earlier cycle, is not enough here.`
                  : `Only acceptable tests taken on cycle ${cycle.cycle_number} after the stop are listed.`
              }
            >
              <Select
                id={resolvingId}
                value={resolvingChosen}
                onChange={(e) => setResolvingTest(e.target.value)}
                placeholder={needsGasResolution ? 'Choose a gas test' : 'None'}
                options={candidates.map((t) => ({
                  value: t.id,
                  label: `${labelFor(GAS_TEST_KINDS, t.test_kind)}, observed ${fmtDateTime(t.reported_observed_at)}, ${t.sample_location}`,
                }))}
              />
            </Field>
          ) : needsGasResolution ? (
            <Banner tone="warn" title="No safe gas test since the unsafe result">
              The AGT must record an acceptable test on cycle {cycle.cycle_number} (for example a check after the stop) before it can
              be validated. A pre-arranged test, or a test on an earlier cycle, is not enough here.
            </Banner>
          ) : null}
        </section>
      ) : null}

      <section className="mb-4">
        <h3 className="display mb-2 text-base font-semibold text-ink">
          Validation period
          <RuleRefs ruleIds={['CORE-056']} />
        </h3>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <DateTimeField label="Valid from" required value={from} onChange={setFrom} />
          <DateTimeField label="Valid to" required value={to} onChange={setTo} />
        </div>
        <p className="text-sm text-ink">
          Length: <strong>{mins !== null ? formatDuration(mins) : '-'}</strong>
        </p>
        {cycle ? (
          <p className="mt-1 text-xs text-ink-soft">
            Must fit inside the cycle window (<WindowText from={cycle.requested_from} to={cycle.requested_to} />)
            {auth ? (
              <>
                {' '}
                and the authorised period (<WindowText from={auth.authorisation_from} to={auth.authorisation_to} />)
              </>
            ) : null}
            .
          </p>
        ) : null}
        {gasPlan ? (
          <p className="mt-1 text-xs text-ink-soft">
            A gas test taken before the validated period does not count for starting work. Valid from starts at the cycle start
            when it can, so a test the AGT took after the cycle request still counts.
          </p>
        ) : null}
        {gasPlan && latestGas && latestGasInPeriod === false ? (
          <Banner tone="warn" title="The latest gas test is outside this period" className="mt-3">
            It was observed {fmtDateTime(latestGas.reported_observed_at)}. It does not count for starting work, so the Permit Holder
            cannot accept until the AGT records a new acceptable test inside the validated period. Start the period earlier (not
            before the cycle start, and not more than {VALIDATION_MAX_BACKDATE_MINUTES} minutes ago) or ask the AGT for a new test.
          </Banner>
        ) : null}
        {mins !== null && mins > normalHours * 60 ? (
          <Banner tone="warn" title={`Longer than the normal ${normalHours}-hour validation period`} className="mt-3">
            A longer period has no approved route yet, so expect the server to refuse it.{' '}
            <PolicyNote param={normalParam} code="validation.normal_hours" />
          </Banner>
        ) : null}
      </section>

      <section className="mb-4">
        <h3 className="display mb-1 text-base font-semibold text-ink">
          The sixteen checks
          <RuleRefs ruleIds={['CORE-055']} />
        </h3>
        <p className="mb-3 text-xs text-ink-soft">
          Answer every check yourself. If you have any doubt, do not validate: report it to the RS.
        </p>
        <ol className="space-y-3">
          {AA_VALIDATION_CHECKS.map((o, i) => {
            const a = answers[o.code] ?? { result: null, note: '' }
            const isGas = o.code === 'gas_tests'
            const options = isGas ? (gasPlan ? GAS_REQUIRED_OPTIONS : GAS_NOT_REQUIRED_OPTIONS) : STANDARD_OPTIONS
            return (
              <li key={o.code} className="rounded-lg border border-line bg-white p-3">
                <Choice<CheckResult>
                  compact
                  name={`aa-check-${o.code}`}
                  legend={
                    <>
                      <span className="text-ink-faint">{i + 1}. </span>
                      {o.label}
                    </>
                  }
                  hint={
                    isGas
                      ? gasPlan
                        ? `The permit requires gas testing (${gasPlan.species.map(speciesLabel).join(', ') || 'no gases listed'}). ${o.help ?? ''}`
                        : 'The permit has no gas testing requirement.'
                      : o.help
                  }
                  options={options}
                  value={a.result}
                  onChange={(v) => setAnswer(o.code, { result: v })}
                />
                {a.result === 'not_applicable' ? (
                  <div className="mt-3">
                    <TextField
                      label="Why it does not apply"
                      required
                      value={a.note}
                      onChange={(note) => setAnswer(o.code, { note })}
                      placeholder={isGas ? 'e.g. No gas testing in the permit' : 'e.g. No isolation in this job'}
                    />
                  </div>
                ) : null}
                {isGas && a.result === 'confirmed' && cycle ? <CycleGasTests cycle={cycle} /> : null}
                {isGas && a.result === 'prearranged' ? (
                  <Prearrangement
                    registry={registry}
                    testers={testers}
                    tester={testerChosen}
                    setTester={setTester}
                    plannedAt={plannedAt}
                    setPlannedAt={setPlannedAt}
                    sampleLocation={sampleLocation}
                    setSampleLocation={setSampleLocation}
                    requiredSpecies={requiredSpecies}
                    optionalSpecies={optionalSpecies}
                    extraSpecies={extraChosen}
                    setExtraSpecies={setExtraSpecies}
                  />
                ) : null}
              </li>
            )
          })}
        </ol>
      </section>

      {needsExam ? (
        <section className="mb-4 rounded-lg border border-line bg-white p-3">
          <h3 className="display mb-1 text-base font-semibold text-ink">
            {classA?.dailyExam ? 'Worksite examination (Class A, every validation)' : 'First-day worksite examination'}
            <RuleRefs ruleIds={['CORE-035']} />
          </h3>
          <p className="mb-3 text-xs text-ink-soft">
            {classA?.dailyExam
              ? 'A Class A permit here needs the validating RS to examine the worksite at every validation. It is signed with this validation.'
              : 'This permit needs the validating RS to examine the worksite on the first day (process facility). It is signed with this validation.'}
          </p>
          <DateTimeField label="When you examined the site" required value={examAt} onChange={setExamAt} />
          <Field label="What you found" required htmlFor={findingsId}>
            <TextArea id={findingsId} value={findings} onChange={(e) => setFindings(e.target.value)} />
          </Field>
        </section>
      ) : null}

      {classA ? (
        <section className="mb-4 rounded-lg border border-class-a bg-white p-3">
          <h3 className="display mb-1 text-base font-semibold text-ink">
            Class A conditions
            <RuleRefs ruleIds={['CORE-039', 'CORE-055']} />
          </h3>
          {classA.hcaUnresolved ? (
            <Banner tone="bad" title="Not decided for hydrocarbon areas" className="mb-3">
              The Class A worksite-examination rule for hydrocarbon areas is not decided (SP-2329 p26 and p43 conflict), so this permit cannot be
              validated here yet.
            </Banner>
          ) : null}
          <TickRow
            checked={staysInArea}
            onChange={setStaysInArea}
            label="I will stay in the work area while this Class A work goes on."
            hint="A Class A cycle can be validated only if the validating RS stays in the area. If you cannot, do not validate."
          />
          {delegating ? (
            classA.delegationAllowed === false ? (
              <Banner tone="bad" title="First gas test by someone else" className="mt-3">
                The first gas test of this cycle is by {classA.otherFirstTester?.name}. For Class A the validating RS does the first test of the
                day, and the rules in use do not allow delegating it. Do the first test yourself in a new cycle.
              </Banner>
            ) : (
              <Field
                label={`First gas test delegated to ${classA.otherFirstTester?.name}: say that you delegated it and why`}
                required
                htmlFor={delegationId}
                hint="For Class A the validating RS does the first gas test of the day unless they delegate it. Your note is signed with this validation."
              >
                <TextArea id={delegationId} value={delegationNote} onChange={(e) => setDelegationNote(e.target.value)} />
              </Field>
            )
          ) : gasPlan ? (
            <p className="mt-2 text-xs text-ink-soft">You do the first gas test of this cycle yourself, so no delegation is recorded.</p>
          ) : null}
        </section>
      ) : null}

      {cycle ? (
        <SigningStatement role={action.role} person={bundle.viewer.person}>
          <p>
            I checked all sixteen items above for cycle {n} myself, and each answer is mine. I validate cycle {n} for the
            period shown.
          </p>
          {gasAnswer === 'prearranged' ? (
            <p>The gas test is pre-arranged with the tester named above. Work must not start until an acceptable test is recorded.</p>
          ) : null}
          {needsExam ? <p>I examined the worksite myself at the time given, and my findings are recorded above.</p> : null}
          {classA ? <p>For this Class A permit I will stay in the work area while the work goes on.</p> : null}
          {classA && delegating && classA.delegationAllowed !== false ? (
            <p>I delegated the first gas test of this cycle to {classA.otherFirstTester?.name}, for the reason I gave above.</p>
          ) : null}
          {needsResolution ? (
            <p>
              I am satisfied the cause of the stop on {stoppedCycles.length === 1 ? 'cycle' : 'cycles'} {joinNumbers(stoppedCycles)} has been dealt with, as
              described above.
            </p>
          ) : null}
          <p>This validation is for cycle {n} only. If I had any doubt, I would not validate and would stop the job going ahead.</p>
        </SigningStatement>
      ) : null}
    </PanelFrame>
  )
}
